import { useGoogleLogin } from "@react-oauth/google";
import { unwrapResult } from "@reduxjs/toolkit";
import React, { useRef } from "react";
import { Link, useHistory } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { requestOK } from "redux-thunk-kit";
import {
  Col,
  Logo,
  Row,
  textVr,
  UiButton,
  UiCheckbox,
  UiDivider,
  UiFormControl,
  UiFormErrorMessage,
  UiFormLabel,
  UiImage,
  UiInput,
  UiInputGroup,
  UiInputLeftElement,
  UiLink,
  UiPrimaryButton,
  UiText,
} from "~/components/elements";
import { PageLayout } from "~/components/layouts/PageLayout";
import PasswordInput from "~/components/shared/PasswordInput";
import { PAGE_URLS, PAGES, SOCIAL_LOGIN_PLATFORMS } from "~/constants";
import { useAppDispatch, useInput, useNotification, useRequestState } from "~/hooks";
import { register, socialLoginMisc } from "~/redux/auth/thunk";
import { getPageQuery } from "~/utils/helpers";
import { MailIcon } from "~/components/icons/SvgIcons";
import googlelogo from "~/assets/img/v2/googlelogo.png";
import { appConfig } from "~/config";

export default function RegisterPage() {
  const dispatch = useAppDispatch();
  const { addMessage } = useNotification(dispatch);
  const history = useHistory();
  const rules = {
    email(value: string) {
      if (inputs.email === "") {
        return {
          presence: true,
        };
      }
      return {
        email: true,
      };
    },
    password(value: string) {
      if (value) {
        return { length: { minimum: 6 } };
      }
      return { presence: true };
    },
    confirmPassword(value: string) {
      if (value) {
        return { length: { minimum: 6 }, equality: "password" };
      }
      return { presence: true };
    },
  };
  const [inputs, setInputs, { validation }] = useInput(
    { email: "", password: "", confirmPassword: "", isAgreed: false },
    { rules }
  );

  const { loading } = useRequestState(register);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [recaptchaToken, setRecaptchaToken] = React.useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e?.preventDefault();
    if (validation.getErrors()) {
      return;
    }

    if (appConfig.recaptchaSiteKey && !recaptchaToken) {
      addMessage("Please complete the reCAPTCHA verification", "error");
      return;
    }

    const resAction = await dispatch(register({ data: { ...inputs, recaptchaToken } }));
    if (requestOK(resAction, register)) {
      const result = unwrapResult(resAction);
      onSignUpSuccess(result.apiResponseData.id);
      recaptchaRef.current?.reset();
      setRecaptchaToken(null);
    } else {
      recaptchaRef.current?.reset();
      setRecaptchaToken(null);
    }
  };

  const handleRecaptchaChange = (token: string | null) => {
    setRecaptchaToken(token);
  };

  const onSignUpSuccess = (userId?: string) => {
    history.replace({
      pathname: PAGE_URLS.EMAIL_CONFIRMATION,
      state: { timer: 60, userId, email: inputs.email },
    });
  };

  const onLoginSuccess = (redirectAfterLogin?: string) => {
    // Try to redirect to the very first URL
    const urlParams = new URL(window.location.href);
    const queryParams = getPageQuery();
    let { redirect } = queryParams as { redirect: string };
    if (redirectAfterLogin) {
      history.replace(redirectAfterLogin);
      return;
    }
    if (redirect) {
      const redirectUrlParams = new URL(redirect);
      if (redirectUrlParams.origin === urlParams.origin) {
        redirect = redirect.substr(urlParams.origin.length);
        if (redirect.match(/^\/.*#/)) {
          redirect = redirect.substr(redirect.indexOf("#") + 1);
        }
      } else {
        window.location.href = "/";
        return;
      }
    }
    history.replace(redirect || PAGES.DASHBOARD);
  };

  const onSignInWithGoogle = useGoogleLogin({
    flow: "auth-code",
    scope: [
      "openid",
      "https://www.googleapis.com/auth/userinfo.profile",
      "https://www.googleapis.com/auth/userinfo.email",
    ].join(" "),
    onSuccess: async resp => {
      const { code } = resp;

      const resAction = await dispatch(
        socialLoginMisc({
          data: {
            flow: "auth-code",
            token: code || "",
            provider: SOCIAL_LOGIN_PLATFORMS.GOOGLE,
            clientId: appConfig.googleClientId,
          },
        })
      );
      if (requestOK(resAction, socialLoginMisc)) {
        onLoginSuccess();
      }
    },
    onError: errorResponse => addMessage(errorResponse.error_description || "", "error"),
  });

  return (
    <PageLayout
      metaTagsConfig={{
        title: `Bettershot - Sign up`,
        description: `Get 120 professional photoshoots in 3 hours using AI. Elevate your personal brand with corporate photoshoots.`,
        charset: "utf8",
        lang: "en",
        metas: [
          {
            name: "keywords",
            content: "corporate photoshoot,professional photoshoot,bettershot ai,linkedin profile photo,ai photoshoot",
          },
        ],
        links: [{ rel: "canonical", href: window.location.href }],
      }}
    >
      <Col alignItems="center">
        <Col mt={{ base: 8, md: 0 }} px={{ base: 4 }} maxW="29.625rem" w="full">
          <Logo containerProps={{ justifyContent: "center" }} />
          <UiText vr="headline3" fontSize={{ base: "1.5rem", md: "1.75rem" }} textAlign="center" mt={12}>
            Sign up
          </UiText>
          <form onSubmit={handleSubmit}>
            <Col mx="auto" mt={4}>
              <UiFormControl isRequired isInvalid={!!validation?.errors?.email}>
                <UiFormLabel {...textVr("body2")}>Email</UiFormLabel>
                <UiInputGroup>
                  <UiInputLeftElement pointerEvents="none">
                    <MailIcon />
                  </UiInputLeftElement>
                  <UiInput
                    ps="10"
                    placeholder="Enter your email"
                    name="email"
                    type="email"
                    defaultValue={inputs.email}
                    _invalid={{ boxShadow: "none", borderColor: "red" }}
                    onChange={({ target }) => setInputs({ email: target.value })}
                  />
                </UiInputGroup>
                {validation?.errors?.email?.[0] && (
                  <UiFormErrorMessage mt={1} fontSize="12px" color="red">
                    {validation?.errors?.email?.[0]}
                  </UiFormErrorMessage>
                )}
              </UiFormControl>

              <UiFormControl {...textVr("body2")} isRequired isInvalid={!!validation?.errors?.password} mt={4}>
                <UiFormLabel fontWeight="400">Password</UiFormLabel>
                <PasswordInput
                  name="password"
                  defaultValue={inputs.password}
                  _invalid={{ boxShadow: "none", borderColor: "red" }}
                  onChange={({ target }) => setInputs({ password: target.value })}
                />
                {validation?.errors?.password?.[0] && (
                  <UiFormErrorMessage mt={1} fontSize="12px" color="red">
                    {validation?.errors?.password?.[0]}
                  </UiFormErrorMessage>
                )}
              </UiFormControl>

              <UiFormControl isRequired isInvalid={!!validation?.errors?.confirmPassword} mt={4}>
                <UiFormLabel fontWeight="400">Confirm Password</UiFormLabel>
                <PasswordInput
                  placeholder="Enter your password again"
                  name="password"
                  defaultValue={inputs.confirmPassword}
                  _invalid={{ boxShadow: "none", borderColor: "red" }}
                  onChange={({ target }) => setInputs({ confirmPassword: target.value })}
                />
                {validation?.errors?.confirmPassword?.[0] && (
                  <UiFormErrorMessage mt={1} fontSize="12px" color="red">
                    {validation?.errors?.confirmPassword?.[0]}
                  </UiFormErrorMessage>
                )}
              </UiFormControl>

              <Row justifyContent="space-between" mt={7}>
                <UiCheckbox onChange={e => setInputs({ isAgreed: e.target.checked })}>
                  <UiText vr="body2">
                    I agree to the{" "}
                    <UiLink as={Link} to={PAGES.TERMS_AND_CONDITIONS}>
                      Terms and Conditions.
                    </UiLink>
                  </UiText>
                </UiCheckbox>
              </Row>
              {appConfig.recaptchaSiteKey && (
                <Col mt={4}>
                  <ReCAPTCHA ref={recaptchaRef} sitekey={appConfig.recaptchaSiteKey} onChange={handleRecaptchaChange} />
                </Col>
              )}
              <UiPrimaryButton
                isLoading={loading}
                type="submit"
                onClick={handleSubmit}
                isDisabled={
                  !inputs.isAgreed ||
                  !(inputs.email && inputs.password && inputs.confirmPassword) ||
                  (!!appConfig.recaptchaSiteKey && !recaptchaToken)
                }
                mt={4}
              >
                Sign up
              </UiPrimaryButton>
            </Col>
          </form>

          <UiText vr="body2" mx="auto" mt={3}>
            Already have an account?
            <UiLink as={Link} to={{ pathname: PAGE_URLS.LOGIN, search: window.location.search }} fontWeight="700">
              &nbsp;Sign in now&nbsp;
            </UiLink>
          </UiText>
          <UiDivider text="Or" containerProps={{ mt: 3 }} />
          <UiButton
            variant="outline"
            leftIcon={<UiImage src={googlelogo} boxSize="6" />}
            onClick={() => onSignInWithGoogle()}
            mt={2}
            borderColor="border"
            bg="transparent"
            color="darkblue"
            _hover={{ bg: "transparent", color: "border" }}
          >
            Sign up with Google
          </UiButton>
        </Col>
      </Col>
    </PageLayout>
  );
}
